/**
 * Client-side document analysis — pure functions, no side effects.
 * All metrics computed from raw text for instant feedback.
 */

export interface WordMetrics {
  wordCount: number;
  charCount: number;
  sentenceCount: number;
  paragraphCount: number;
}

export interface SectionInfo {
  id: string;
  title: string;
  line: number;
  wordCount: number;
}

export interface DocumentMetrics {
  wordCount: number;
  charCount: number;
  sentenceCount: number;
  paragraphCount: number;
  sectionCount: number;
  readabilityScore: number;
  avgSentenceLength: number;
  avgWordLength: number;
  passiveVoicePercent: number;
  citationCount: number;
  citationsPerSection: Record<string, number>;
  sectionWordCounts: Record<string, number>;
  sectionCompleteness: Record<string, number>;
  writingVelocity: number;
  submissionReadiness: number;
}

// ── Word Metrics ──

function stripLatexCommands(text: string): string {
  return text
    .replace(/\\[a-zA-Z]+\{[^}]*\}/g, " ")
    .replace(/\\[a-zA-Z]+/g, " ")
    .replace(/[{}$\\%&_^~]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function stripHtmlTags(text: string): string {
  return text
    .replace(/<[^>]*>/g, " ")
    .replace(/&[a-z]+;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function computeWordMetrics(text: string): WordMetrics {
  const cleaned = stripLatexCommands(text);
  const words = cleaned.split(/\s+/).filter((w) => w.length > 0);
  const sentences = cleaned.split(/[.!?]+/).filter((s) => s.trim().length > 0);
  const paragraphs = text.split(/\n\s*\n/).filter((p) => p.trim().length > 0);

  return {
    wordCount: words.length,
    charCount: cleaned.replace(/\s/g, "").length,
    sentenceCount: Math.max(1, sentences.length),
    paragraphCount: Math.max(1, paragraphs.length),
  };
}

// ── Readability (Flesch-Kincaid) ──

function countSyllables(word: string): number {
  const w = word.toLowerCase().replace(/[^a-z]/g, "");
  if (w.length <= 3) return 1;

  let count = 0;
  const vowels = "aeiouy";
  let prevVowel = false;

  for (const char of w) {
    const isVowel = vowels.includes(char);
    if (isVowel && !prevVowel) count++;
    prevVowel = isVowel;
  }

  if (w.endsWith("e")) count = Math.max(1, count - 1);
  if (w.endsWith("le") && w.length > 2 && !vowels.includes(w[w.length - 3])) count++;

  return Math.max(1, count);
}

export function computeReadability(text: string): number {
  const cleaned = stripLatexCommands(text);
  const words = cleaned.split(/\s+/).filter((w) => w.length > 0);
  const sentences = cleaned.split(/[.!?]+/).filter((s) => s.trim().length > 0);

  if (words.length === 0 || sentences.length === 0) return 0;

  const totalSyllables = words.reduce((sum, w) => sum + countSyllables(w), 0);
  const avgWordsPerSentence = words.length / sentences.length;
  const avgSyllablesPerWord = totalSyllables / words.length;

  // Flesch Reading Ease: 206.835 - 1.015 * ASL - 84.6 * ASW
  const score = 206.835 - 1.015 * avgWordsPerSentence - 84.6 * avgSyllablesPerWord;
  return Math.max(0, Math.min(100, Math.round(score)));
}

// ── Section Metrics ──

export function computeSectionMetrics(
  text: string,
  docType: "latex" | "markdown" | "docx",
): SectionInfo[] {
  const lines = text.split("\n");
  const sections: SectionInfo[] = [];

  const regex = docType === "latex"
    ? /\\(section|subsection|subsubsection)\*?\{([^}]*)\}/
    : docType === "docx"
      ? /<h([1-6])[^>]*>(.*?)<\/h\1>/i
      : /^(#{1,6})\s+(.+)/;

  for (let i = 0; i < lines.length; i++) {
    const match = regex.exec(lines[i]);
    if (match) {
      const title = docType === "docx" ? match[2].replace(/<[^>]*>/g, "").trim() : match[2].trim();
      sections.push({
        id: `section-${i}`,
        title,
        line: i + 1,
        wordCount: 0,
      });
    }
  }

  for (let s = 0; s < sections.length; s++) {
    const startLine = sections[s].line;
    const endLine = s < sections.length - 1 ? sections[s + 1].line - 1 : lines.length;
    const sectionText = lines.slice(startLine, endLine).join("\n");
    sections[s].wordCount = computeWordMetrics(sectionText).wordCount;
  }

  return sections;
}

// ── Passive Voice Detection ──

const PASSIVE_PATTERNS = [
  /\b(?:is|are|was|were|be|been|being)\s+\w+(?:ed|en)\b/gi,
  /\b(?:has|have|had)\s+been\s+\w+(?:ed|en)\b/gi,
  /\b(?:will|shall|would|should|could|might|must)\s+be\s+\w+(?:ed|en)\b/gi,
];

export function detectPassiveVoice(text: string): number {
  const cleaned = stripLatexCommands(text);
  const sentences = cleaned.split(/[.!?]+/).filter((s) => s.trim().length > 5);
  if (sentences.length === 0) return 0;

  let passiveCount = 0;
  for (const sentence of sentences) {
    for (const pattern of PASSIVE_PATTERNS) {
      pattern.lastIndex = 0;
      if (pattern.test(sentence)) {
        passiveCount++;
        break;
      }
    }
  }

  return Math.round((passiveCount / sentences.length) * 100);
}

// ── Citation Count ──

export function countCitations(
  text: string,
  docType: "latex" | "markdown" | "docx",
): { total: number; perSection: Record<string, number> } {
  const sections = computeSectionMetrics(text, docType);
  const lines = text.split("\n");

  const citeRegex = docType === "latex"
    ? /\\cite[tp]?\{[^}]+\}/g
    : docType === "docx"
      ? /<a\s[^>]*href[^>]*>/gi
      : /\[@[^\]]+\]/g;

  const total = (text.match(citeRegex) || []).length;
  const perSection: Record<string, number> = {};

  for (let s = 0; s < sections.length; s++) {
    const startLine = sections[s].line - 1;
    const endLine = s < sections.length - 1 ? sections[s + 1].line - 1 : lines.length;
    const sectionText = lines.slice(startLine, endLine).join("\n");
    perSection[sections[s].title] = (sectionText.match(citeRegex) || []).length;
  }

  return { total, perSection };
}

// ── Submission Readiness Score ──

export function computeReadiness(
  metrics: Omit<DocumentMetrics, "submissionReadiness">,
): number {
  let score = 0;

  // Word count contribution (up to 25 pts)
  if (metrics.wordCount >= 500) score += 25;
  else if (metrics.wordCount >= 200) score += 15;
  else if (metrics.wordCount >= 50) score += 5;

  // Readability contribution (up to 20 pts)
  if (metrics.readabilityScore >= 30 && metrics.readabilityScore <= 70) score += 20;
  else if (metrics.readabilityScore > 0) score += 10;

  // Section structure (up to 20 pts)
  if (metrics.sectionCount >= 3) score += 20;
  else if (metrics.sectionCount >= 1) score += 10;

  // Citations (up to 15 pts)
  if (metrics.citationCount >= 5) score += 15;
  else if (metrics.citationCount >= 1) score += 8;

  // Passive voice penalty (up to 10 pts)
  if (metrics.passiveVoicePercent <= 15) score += 10;
  else if (metrics.passiveVoicePercent <= 30) score += 5;

  // Balanced sections (up to 10 pts)
  const wordCounts = Object.values(metrics.sectionWordCounts);
  if (wordCounts.length >= 2) {
    const avg = wordCounts.reduce((a, b) => a + b, 0) / wordCounts.length;
    const variance = wordCounts.reduce((sum, c) => sum + (c - avg) ** 2, 0) / wordCounts.length;
    const cv = avg > 0 ? Math.sqrt(variance) / avg : 1;
    if (cv < 0.5) score += 10;
    else if (cv < 1) score += 5;
  }

  return Math.min(100, score);
}

// ── Full Analysis ──

export function analyzeDocument(
  text: string,
  docType: "latex" | "markdown" | "docx",
  writingVelocity = 0,
): DocumentMetrics {
  const wordMetrics = computeWordMetrics(text);
  const readabilityScore = computeReadability(text);
  const sections = computeSectionMetrics(text, docType);
  const passiveVoicePercent = detectPassiveVoice(text);
  const citations = countCitations(text, docType);

  const sectionWordCounts: Record<string, number> = {};
  const sectionCompleteness: Record<string, number> = {};
  const avgSectionWords = sections.length > 0
    ? sections.reduce((sum, s) => sum + s.wordCount, 0) / sections.length
    : 0;

  for (const s of sections) {
    sectionWordCounts[s.title] = s.wordCount;
    sectionCompleteness[s.title] = avgSectionWords > 0
      ? Math.min(1, s.wordCount / Math.max(avgSectionWords, 50))
      : 0;
  }

  const cleaned = docType === "docx" ? stripHtmlTags(text) : stripLatexCommands(text);
  const words = cleaned.split(/\s+/).filter((w) => w.length > 0);
  const avgWordLength = words.length > 0
    ? words.reduce((sum, w) => sum + w.length, 0) / words.length
    : 0;

  const partial: Omit<DocumentMetrics, "submissionReadiness"> = {
    ...wordMetrics,
    sectionCount: sections.length,
    readabilityScore,
    avgSentenceLength: wordMetrics.sentenceCount > 0
      ? wordMetrics.wordCount / wordMetrics.sentenceCount
      : 0,
    avgWordLength,
    passiveVoicePercent,
    citationCount: citations.total,
    citationsPerSection: citations.perSection,
    sectionWordCounts,
    sectionCompleteness,
    writingVelocity,
  };

  return {
    ...partial,
    submissionReadiness: computeReadiness(partial),
  };
}
